// Setup item preview popover with hover/click interactions
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  safePolygon,
} from '@floating-ui/react';
import type { BrandConfig } from '@lds-efsp/brand-config';

// Detect touch device - only consider actual mobile/tablet devices, not laptops with trackpads
const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  // Check user agent for actual mobile/tablet devices
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTablet = /ipad|android(?!.*mobile)/i.test(userAgent);
  // Only consider it a touch device if it's a mobile/tablet AND has touch capability
  return (isMobile || isTablet) && ('ontouchstart' in window);
};

interface PreviewField {
  label: string;
  value: string | React.ReactNode;
}

interface PreviewContentProps {
  brand: BrandConfig;
  title: string;
  fields: PreviewField[];
}

const PreviewContent: React.FC<PreviewContentProps> = ({ brand, title, fields }) => (
  <div
    className="rounded-lg shadow-lg"
    style={{
      backgroundColor: 'white',
      border: `1px solid ${brand.colors.border}`,
      minWidth: '220px',
      maxWidth: '320px',
    }}
  >
    {/* Header */}
    <div
      className="px-3 py-2 border-b font-medium text-sm"
      style={{
        borderColor: brand.colors.border,
        color: brand.colors.secondary,
      }}
    >
      {title}
    </div>

    {/* Fields */}
    <div className="px-3 py-2 space-y-1.5">
      {fields.map((field) => (
        <div key={field.label} className="flex justify-between gap-3 text-sm">
          <span style={{ color: brand.colors.textLight }}>{field.label}</span>
          <span
            className="text-right font-medium truncate"
            style={{ color: brand.colors.text }}
          >
            {field.value || '-'}
          </span>
        </div>
      ))}
    </div>
  </div>
);

interface SetupItemPreviewPopoverProps {
  brand: BrandConfig;
  title: string;
  fields: PreviewField[];
  triggerClassName?: string;
}

const SetupItemPreviewPopover: React.FC<SetupItemPreviewPopoverProps> = ({
  brand,
  title,
  fields,
  triggerClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const [isTouch] = useState(isTouchDevice);

  // Track hover intent with delay
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && !isTouch,
    onOpenChange: (open) => {
      if (!isPinned) {
        setIsOpen(open);
      }
    },
    middleware: [
      offset(8),
      flip({ fallbackAxisSideDirection: 'end' }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
  });

  const hover = useHover(context, {
    move: false,
    enabled: !isTouch && !isPinned,
    delay: { open: 300, close: 150 },
    handleClose: safePolygon({
      blockPointerEvents: true,
    }),
  });

  const focus = useFocus(context, {
    enabled: !isTouch && !isPinned,
  });

  const dismiss = useDismiss(context, {
    enabled: !isPinned,
  });

  const role = useRole(context, { role: 'dialog' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  // Handle click - pin on desktop, open sheet on mobile
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    if (isTouch) {
      setIsMobileSheetOpen(true);
    } else {
      if (isOpen) {
        setIsPinned(!isPinned);
      } else {
        setIsOpen(true);
        setIsPinned(true);
      }
    }
  }, [isTouch, isOpen, isPinned]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e as unknown as React.MouseEvent);
    } else if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      setIsOpen(false);
      setIsPinned(false);
    }
  }, [handleClick, isOpen]);

  // Close on outside click when pinned
  useEffect(() => {
    if (!isPinned) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const floating = refs.floating.current;
      const reference = refs.reference.current as Node | null;

      if (
        floating &&
        !floating.contains(target) &&
        reference &&
        !reference.contains(target)
      ) {
        setIsOpen(false);
        setIsPinned(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPinned, refs.floating, refs.reference]);

  // Cleanup timeout on unmount
  useEffect(() => {
    const timeoutRef = hoverTimeoutRef.current;
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, []);

  return (
    <>
      {/* Trigger button */}
      <button
        ref={refs.setReference}
        {...getReferenceProps({
          onClick: handleClick,
          onKeyDown: handleKeyDown,
        })}
        className={`inline-flex items-center justify-center p-1 rounded hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${triggerClassName}`}
        style={{
          color: isOpen || isPinned ? brand.colors.link : brand.colors.textLight,
          // @ts-expect-error CSS custom property
          '--tw-ring-color': brand.colors.primary,
        }}
        aria-label="View item details"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-testid="setup-item-preview-trigger"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>

      {/* Desktop popover */}
      {isOpen && !isTouch && (
        <FloatingPortal>
          {/* eslint-disable react-hooks/refs -- refs.setFloating is a callback ref from @floating-ui/react */}
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50"
            data-testid="setup-item-preview-popover"
          >
          {/* eslint-enable react-hooks/refs */}
            <PreviewContent brand={brand} title={title} fields={fields} />
          </div>
        </FloatingPortal>
      )}

      {/* Mobile bottom sheet */}
      {isMobileSheetOpen && (
        <div className="fixed inset-0 z-50 flex flex-col">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileSheetOpen(false)}
            aria-hidden="true"
          />

          {/* Bottom sheet */}
          <div
            className="relative mt-auto bg-white rounded-t-2xl shadow-xl max-h-[80vh] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="preview-sheet-title"
          >
            {/* Handle */}
            <div className="flex justify-center py-2">
              <div
                className="w-10 h-1 rounded-full"
                style={{ backgroundColor: brand.colors.border }}
              />
            </div>

            {/* Header */}
            <div
              className="px-4 py-3 flex items-center justify-between border-b"
              style={{ borderColor: brand.colors.border }}
            >
              <h2
                id="preview-sheet-title"
                className="text-lg font-semibold"
                style={{ color: brand.colors.secondary }}
              >
                {title}
              </h2>
              <button
                onClick={() => setIsMobileSheetOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4">
              <div className="space-y-3">
                {fields.map((field) => (
                  <div
                    key={field.label}
                    className="flex justify-between gap-4 py-2 border-b"
                    style={{ borderColor: brand.colors.border }}
                  >
                    <span
                      className="text-sm"
                      style={{ color: brand.colors.textLight }}
                    >
                      {field.label}
                    </span>
                    <span
                      className="text-sm font-medium text-right"
                      style={{ color: brand.colors.text }}
                    >
                      {field.value || '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SetupItemPreviewPopover;
